import {
  AccountCircle as AccountCircleIcon,
  Add as AddIcon,
  AdminPanelSettings as AdminIcon,
  Check as CheckIcon,
  ContentCopy as CopyIcon,
  DarkMode as DarkModeIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Groups as GroupsIcon,
  HelpOutline as HelpOutlineIcon,
  ExitToApp as LeaveIcon,
  LightMode as LightModeIcon,
  Link as LinkIcon,
  Palette as PaletteIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemSecondaryAction,
  ListItemText,
  Snackbar,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppDialog from '../components/AppDialog';
import GroupFormDialog, { GROUP_COLORS } from '../components/GroupFormDialog';
import { useAuth } from '../contexts/AuthContext';
import { useGroups } from '../contexts/GroupContext';
import { useThemeMode } from '../contexts/ThemeContext';
import { useThemeColors } from '../hooks/useThemeColors';
import {
  createGroup,
  createInvite,
  deleteGroup,
  getGroupMembers,
  isGroupOwner,
  removeGroupMember,
  updateGroup,
  updateMemberRole,
} from '../services/groupService';
import { IMG } from '../theme/assets';
import type { Group, GroupMember } from '../types';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

// カラーパレット
const Settings: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { groups, refreshGroups } = useGroups();
  const { themeMode, setThemeMode } = useThemeMode();
  const tokens = useThemeColors();

  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);

  // グループ作成ダイアログ
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDescription, setNewGroupDescription] = useState('');
  const [newGroupColor, setNewGroupColor] = useState(GROUP_COLORS[0]);

  // グループ編集ダイアログ
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<Group | null>(null);
  const [editGroupName, setEditGroupName] = useState('');
  const [editGroupDescription, setEditGroupDescription] = useState('');
  const [editGroupColor, setEditGroupColor] = useState('');

  // メンバー管理ダイアログ
  const [membersDialogOpen, setMembersDialogOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);
  const [members, setMembers] = useState<GroupMember[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);

  // 招待リンクダイアログ
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteLinks, setInviteLinks] = useState<string[]>([]);
  const [inviteCount, setInviteCount] = useState(1);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteGenerated, setInviteGenerated] = useState(false);

  // スナックバー
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // グループ削除確認ダイアログ
  const [deleteGroupDialogOpen, setDeleteGroupDialogOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState<Group | null>(null);
  const [deletingGroup, setDeletingGroup] = useState(false);

  // グループオーナーチェック用
  const [ownershipMap, setOwnershipMap] = useState<Record<string, boolean>>({});

  const checkOwnership = useCallback(async () => {
    if (!user) return;
    const map: Record<string, boolean> = {};
    for (const group of groups) {
      map[group.id] = await isGroupOwner(group.id, user.uid);
    }
    setOwnershipMap(map);
  }, [user?.uid, user, groups]);

  useEffect(() => {
    if (user && groups.length > 0) {
      checkOwnership();
    }
  }, [user, groups, checkOwnership]);

  // グループ作成
  const handleCreateGroup = async () => {
    if (!user || !newGroupName.trim()) return;

    try {
      setLoading(true);
      await createGroup(
        user.uid,
        {
          name: newGroupName.trim(),
          description: newGroupDescription.trim() || undefined,
          color: newGroupColor,
        },
        {
          displayName: user.displayName || 'Unknown User',
          photoURL: user.photoURL || undefined,
        },
      );

      await refreshGroups();
      setCreateDialogOpen(false);
      setNewGroupName('');
      setNewGroupDescription('');
      setNewGroupColor(GROUP_COLORS[0]);
      setSnackbar({ open: true, message: 'グループを作成しました', severity: 'success' });
    } catch (error) {
      console.error('Error creating group:', error);
      setSnackbar({ open: true, message: 'グループの作成に失敗しました', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // グループ編集
  const handleOpenEditDialog = (group: Group) => {
    setEditingGroup(group);
    setEditGroupName(group.name);
    setEditGroupDescription(group.description || '');
    setEditGroupColor(group.color);
    setEditDialogOpen(true);
  };

  const handleUpdateGroup = async () => {
    if (!editingGroup || !editGroupName.trim()) return;

    try {
      setLoading(true);
      await updateGroup(editingGroup.id, {
        name: editGroupName.trim(),
        description: editGroupDescription.trim() || undefined,
        color: editGroupColor,
      });

      await refreshGroups();
      setEditDialogOpen(false);
      setEditingGroup(null);
      setSnackbar({ open: true, message: 'グループを更新しました', severity: 'success' });
    } catch (error) {
      console.error('Error updating group:', error);
      setSnackbar({ open: true, message: 'グループの更新に失敗しました', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // グループ削除確認ダイアログを開く
  const handleOpenDeleteGroupDialog = (group: Group) => {
    setGroupToDelete(group);
    setDeleteGroupDialogOpen(true);
  };

  // グループ削除実行
  const handleDeleteGroup = async () => {
    if (!groupToDelete) return;

    try {
      setDeletingGroup(true);
      await deleteGroup(groupToDelete.id);
      await refreshGroups();
      setDeleteGroupDialogOpen(false);
      setGroupToDelete(null);
      setSnackbar({ open: true, message: 'グループを削除しました', severity: 'success' });
    } catch (error) {
      console.error('Error deleting group:', error);
      setSnackbar({ open: true, message: 'グループの削除に失敗しました', severity: 'error' });
    } finally {
      setDeletingGroup(false);
    }
  };

  // メンバー管理
  const handleOpenMembersDialog = async (group: Group) => {
    setSelectedGroup(group);
    setMembersDialogOpen(true);
    setMembersLoading(true);

    try {
      const groupMembers = await getGroupMembers(group.id);
      setMembers(groupMembers);
    } catch (error) {
      console.error('Error loading members:', error);
      setSnackbar({ open: true, message: 'メンバー一覧の取得に失敗しました', severity: 'error' });
    } finally {
      setMembersLoading(false);
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    if (!selectedGroup || !confirm('このメンバーを削除しますか？')) return;

    try {
      await removeGroupMember(selectedGroup.id, memberId);
      setMembers((prev) => prev.filter((m) => m.id !== memberId));
      setSnackbar({ open: true, message: 'メンバーを削除しました', severity: 'success' });
    } catch (error) {
      console.error('Error removing member:', error);
      setSnackbar({ open: true, message: 'メンバーの削除に失敗しました', severity: 'error' });
    }
  };

  const handlePromoteToOwner = async (memberId: string) => {
    if (!selectedGroup || !confirm('このメンバーをオーナーに昇格しますか？')) return;

    try {
      await updateMemberRole(selectedGroup.id, memberId, 'owner');
      setMembers((prev) => prev.map((m) => (m.id === memberId ? { ...m, role: 'owner' } : m)));
      setSnackbar({ open: true, message: 'メンバーをオーナーに昇格しました', severity: 'success' });
    } catch (error) {
      console.error('Error promoting member:', error);
      setSnackbar({ open: true, message: '権限の変更に失敗しました', severity: 'error' });
    }
  };

  // 招待リンクダイアログを開く
  const handleOpenInviteDialog = (group: Group) => {
    setSelectedGroup(group);
    setInviteDialogOpen(true);
    setInviteLinks([]);
    setInviteCount(1);
    setInviteGenerated(false);
  };

  // 招待リンク生成
  const handleCreateInvites = async () => {
    if (!user || !selectedGroup) return;

    setInviteLoading(true);

    try {
      const links: string[] = [];
      for (let i = 0; i < inviteCount; i++) {
        const invite = await createInvite(selectedGroup.id, user.uid);
        const link = `${window.location.origin}/invite/${selectedGroup.id}/${invite.id}`;
        links.push(link);
      }
      setInviteLinks(links);
      setInviteGenerated(true);
      setSnackbar({
        open: true,
        message: `${inviteCount}件の招待リンクを生成しました`,
        severity: 'success',
      });
    } catch (error) {
      console.error('Error creating invites:', error);
      setSnackbar({ open: true, message: '招待リンクの作成に失敗しました', severity: 'error' });
    } finally {
      setInviteLoading(false);
    }
  };

  const handleCopyInviteLink = (link: string) => {
    navigator.clipboard.writeText(link);
    setSnackbar({ open: true, message: '招待リンクをコピーしました', severity: 'success' });
  };

  const handleCopyAllInviteLinks = () => {
    navigator.clipboard.writeText(inviteLinks.join('\n'));
    setSnackbar({
      open: true,
      message: `${inviteLinks.length}件の招待リンクを全てコピーしました`,
      severity: 'success',
    });
  };

  // グループ脱退
  const handleLeaveGroup = async (group: Group) => {
    if (!user) return;
    if (!confirm(`「${group.name}」から脱退しますか？`)) return;

    try {
      setLoading(true);
      await removeGroupMember(group.id, user.uid);
      await refreshGroups();
      setSnackbar({ open: true, message: 'グループから脱退しました', severity: 'success' });
    } catch (error) {
      console.error('Error leaving group:', error);
      setSnackbar({ open: true, message: '脱退に失敗しました', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // 各タブ見出しのヒーローバナー（mock .banner 準拠）
  const renderBanner = (img: string, title: string, desc: string, action?: React.ReactNode) => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.8,
        background: `linear-gradient(120deg, ${tokens.heroFrom}, ${tokens.heroTo})`,
        border: `1px solid ${tokens.line}`,
        borderRadius: '12px',
        p: '14px 18px',
        mb: 2.5,
        boxShadow: '0 6px 18px rgba(30,30,70,.05)',
      }}
    >
      <Box
        component="img"
        src={img}
        alt=""
        sx={{
          width: 54,
          height: 54,
          objectFit: 'contain',
          flexShrink: 0,
          filter: 'drop-shadow(0 5px 10px rgba(30,30,70,.14))',
        }}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 16, fontWeight: 900, color: tokens.ink, m: 0 }}>
          {title}
        </Typography>
        <Typography sx={{ fontSize: 12, color: tokens.muted, mt: '3px', lineHeight: 1.5 }}>
          {desc}
        </Typography>
      </Box>
      {action}
    </Box>
  );

  // グループカードのアクションボタン共通スタイル（mock .iact 準拠）
  const iactSx = {
    width: 34,
    height: 34,
    borderRadius: '9px',
    border: `1px solid ${tokens.line}`,
    bgcolor: tokens.paper,
    color: tokens.muted,
    transition: '.12s',
    '&:hover': { borderColor: tokens.teal, color: tokens.teal, bgcolor: tokens.paper },
    '& svg': { fontSize: 18 },
  } as const;

  // 主要アクション（メンバー管理・招待）はラベル付きボタンで明示する
  const labelBtnSx = {
    textTransform: 'none',
    fontWeight: 800,
    fontSize: 12.5,
    color: tokens.muted,
    border: `1px solid ${tokens.line}`,
    borderRadius: '9px',
    px: 1.25,
    py: 0.5,
    minWidth: 0,
    bgcolor: tokens.paper,
    transition: '.12s',
    '&:hover': { borderColor: tokens.teal, color: tokens.teal, bgcolor: tokens.paper },
    '& .MuiButton-startIcon': { mr: 0.5 },
    '& svg': { fontSize: 17 },
  } as const;

  return (
    <Box sx={{ px: 4, py: 2 }}>
      <Box sx={{ maxWidth: 880, mx: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
          <Box
            component="img"
            src={IMG.settings.owlGear}
            alt=""
            sx={{ width: 40, height: 40, objectFit: 'contain' }}
          />
          <Typography sx={{ fontSize: 22, fontWeight: 900, color: tokens.ink }}>設定</Typography>
        </Box>

        <Tabs
          value={tabValue}
          onChange={(_, newValue) => setTabValue(newValue)}
          sx={{
            borderBottom: `1px solid ${tokens.line}`,
            minHeight: 'auto',
            '& .MuiTabs-indicator': { backgroundColor: tokens.teal, height: '2.5px' },
            '& .MuiTab-root': {
              color: tokens.muted,
              fontWeight: 800,
              fontSize: 13.5,
              textTransform: 'none',
              minHeight: 'auto',
              py: 1.4,
              px: 2,
            },
            '& .MuiTab-root.Mui-selected': { color: tokens.teal },
          }}
        >
          <Tab
            icon={<GroupsIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label="グループ管理"
          />
          <Tab icon={<PaletteIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="外観" />
          <Tab
            icon={<AccountCircleIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label="アカウント"
          />
          <Tab
            icon={<HelpOutlineIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label="ヘルプ"
            onClick={() => navigate('/help')}
          />
        </Tabs>

        {/* グループ管理タブ */}
        <TabPanel value={tabValue} index={0}>
          {renderBanner(
            IMG.settings.group,
            '所属グループ',
            'グループを作成・編集し、メンバーと検証を共有できます。',
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateDialogOpen(true)}
              sx={{
                ml: 'auto',
                flexShrink: 0,
                whiteSpace: 'nowrap',
                background: `linear-gradient(120deg, ${tokens.teal}, ${tokens.indigo})`,
                color: '#fff',
                fontWeight: 800,
                fontSize: 13,
                borderRadius: '11px',
                px: 1.9,
                py: 1.1,
                boxShadow: '0 8px 20px rgba(40,50,90,.18)',
                textTransform: 'none',
                '&:hover': {
                  background: `linear-gradient(120deg, ${tokens.teal}, ${tokens.indigo})`,
                  filter: 'brightness(1.05)',
                  boxShadow: '0 8px 20px rgba(40,50,90,.18)',
                },
              }}
            >
              新しいグループを作成
            </Button>,
          )}

          {groups.length === 0 ? (
            <Alert severity="info">
              まだグループに所属していません。新しいグループを作成するか、招待リンクからグループに参加してください。
            </Alert>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {groups.map((group) => {
                const isOwner = ownershipMap[group.id];
                return (
                  <Box
                    key={group.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.9,
                      bgcolor: tokens.paper,
                      border: `1px solid ${tokens.line}`,
                      borderRadius: '12px',
                      p: '16px 18px',
                      boxShadow: '0 5px 16px rgba(30,30,70,.05)',
                    }}
                  >
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '13px',
                        bgcolor: group.color,
                        display: 'grid',
                        placeItems: 'center',
                        color: '#fff',
                        fontWeight: 900,
                        fontSize: 20,
                        flexShrink: 0,
                      }}
                    >
                      {group.name.charAt(0).toUpperCase()}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.1 }}>
                        <Typography sx={{ fontSize: 15, fontWeight: 900, color: tokens.ink }}>
                          {group.name}
                        </Typography>
                        <Box
                          sx={{
                            fontSize: 10.5,
                            fontWeight: 900,
                            px: '9px',
                            py: '2px',
                            borderRadius: '999px',
                            ...(isOwner
                              ? { bgcolor: 'rgba(91,86,224,0.14)', color: tokens.indigo }
                              : { bgcolor: 'rgba(100,116,139,0.14)', color: tokens.muted }),
                          }}
                        >
                          {isOwner ? 'オーナー' : 'メンバー'}
                        </Box>
                      </Box>
                      {group.description && (
                        <Typography sx={{ fontSize: 12, color: tokens.muted, mt: '3px' }}>
                          {group.description}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                      {isOwner ? (
                        <>
                          <Button
                            onClick={() => handleOpenMembersDialog(group)}
                            startIcon={<PersonIcon />}
                            sx={labelBtnSx}
                          >
                            メンバー
                          </Button>
                          <Button
                            onClick={() => handleOpenInviteDialog(group)}
                            startIcon={<LinkIcon />}
                            sx={labelBtnSx}
                          >
                            招待
                          </Button>
                          <Tooltip title="編集">
                            <IconButton onClick={() => handleOpenEditDialog(group)} sx={iactSx}>
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="削除">
                            <IconButton
                              onClick={() => handleOpenDeleteGroupDialog(group)}
                              sx={{
                                ...iactSx,
                                '&:hover': {
                                  borderColor: '#dc2626',
                                  color: '#dc2626',
                                  bgcolor: tokens.paper,
                                },
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      ) : (
                        <Tooltip title="脱退">
                          <IconButton
                            onClick={() => handleLeaveGroup(group)}
                            sx={{
                              ...iactSx,
                              '&:hover': {
                                borderColor: '#d97706',
                                color: '#d97706',
                                bgcolor: tokens.paper,
                              },
                            }}
                          >
                            <LeaveIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </TabPanel>

        {/* 外観タブ */}
        <TabPanel value={tabValue} index={1}>
          {renderBanner(
            IMG.settings.appearance,
            'テーマ設定',
            'アプリ全体の配色を選べます。お好みのモードをどうぞ。',
          )}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, maxWidth: 520 }}>
            {/* ライトモード */}
            <Box
              onClick={() => setThemeMode('light')}
              sx={{
                position: 'relative',
                border: '2px solid',
                borderColor: themeMode === 'light' ? tokens.teal : tokens.line,
                borderRadius: '13px',
                p: 2.2,
                cursor: 'pointer',
                transition: '.15s',
                background:
                  themeMode === 'light' ? 'linear-gradient(180deg,#f1fbf8,#fff)' : tokens.paper,
                '&:hover': {
                  borderColor: themeMode === 'light' ? tokens.teal : 'rgba(13,148,136,.45)',
                },
              }}
            >
              <Box
                sx={{
                  height: 74,
                  borderRadius: '11px',
                  mb: 1.6,
                  border: `1px solid ${tokens.line}`,
                  background: 'linear-gradient(135deg,#f5f7fa,#e4e8f0)',
                }}
              />
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.1,
                  fontSize: 14,
                  fontWeight: 900,
                  color: tokens.ink,
                }}
              >
                <LightModeIcon sx={{ fontSize: 18, color: tokens.teal }} />
                ライトモード
              </Box>
              <Typography sx={{ fontSize: 11.5, color: tokens.muted, mt: 0.5 }}>
                明るい配色。日中の利用におすすめ。
              </Typography>
              {themeMode === 'light' && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 14,
                    right: 14,
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    bgcolor: tokens.teal,
                    display: 'grid',
                    placeItems: 'center',
                  }}
                >
                  <CheckIcon sx={{ fontSize: 13, color: '#fff' }} />
                </Box>
              )}
            </Box>

            {/* ダークモード */}
            <Box
              onClick={() => setThemeMode('dark')}
              sx={{
                position: 'relative',
                border: '2px solid',
                borderColor: themeMode === 'dark' ? tokens.teal : tokens.line,
                borderRadius: '13px',
                p: 2.2,
                cursor: 'pointer',
                transition: '.15s',
                background:
                  themeMode === 'dark'
                    ? 'linear-gradient(180deg, rgba(45,212,191,0.16), rgba(255,255,255,0.03))'
                    : tokens.paper,
                '&:hover': {
                  borderColor: themeMode === 'dark' ? tokens.teal : 'rgba(13,148,136,.45)',
                },
              }}
            >
              <Box
                sx={{
                  height: 74,
                  borderRadius: '11px',
                  mb: 1.6,
                  border: `1px solid ${tokens.line}`,
                  background: 'linear-gradient(135deg,#1a1a2e,#16213e 60%,#0f3460)',
                }}
              />
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.1,
                  fontSize: 14,
                  fontWeight: 900,
                  color: tokens.ink,
                }}
              >
                <DarkModeIcon sx={{ fontSize: 18, color: tokens.teal }} />
                ダークモード
              </Box>
              <Typography sx={{ fontSize: 11.5, color: tokens.muted, mt: 0.5 }}>
                暗い配色。夜間や目の負担軽減に。
              </Typography>
              {themeMode === 'dark' && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 14,
                    right: 14,
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    bgcolor: tokens.teal,
                    display: 'grid',
                    placeItems: 'center',
                  }}
                >
                  <CheckIcon sx={{ fontSize: 13, color: '#fff' }} />
                </Box>
              )}
            </Box>
          </Box>
        </TabPanel>

        {/* アカウントタブ */}
        <TabPanel value={tabValue} index={2}>
          {renderBanner(
            IMG.settings.account,
            'アカウント情報',
            'ログイン中のアカウントの基本情報です。',
          )}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2.2,
              bgcolor: tokens.paper,
              border: `1px solid ${tokens.line}`,
              borderRadius: '13px',
              p: '22px 24px',
              boxShadow: '0 6px 18px rgba(30,30,70,.05)',
              maxWidth: 520,
            }}
          >
            <Avatar
              src={user?.photoURL || undefined}
              sx={{
                width: 80,
                height: 80,
                background: 'linear-gradient(135deg,#34d399,#10b981)',
                color: '#053b2b',
                fontSize: 30,
                fontWeight: 900,
                border: '3px solid #fff',
                boxShadow: '0 6px 16px rgba(16,185,129,.3)',
                flexShrink: 0,
              }}
            >
              {user?.displayName?.charAt(0) || 'U'}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 18, fontWeight: 900, color: tokens.ink }}>
                {user?.displayName || 'Unknown User'}
              </Typography>
              <Typography sx={{ fontSize: 13, color: tokens.muted, mt: 0.5 }}>
                {user?.email}
              </Typography>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.75,
                  mt: 1.2,
                  fontSize: 11.5,
                  fontWeight: 800,
                  bgcolor: 'rgba(100,116,139,0.12)',
                  borderRadius: '999px',
                  px: 1.4,
                  py: 0.5,
                  color: tokens.muted,
                }}
              >
                {user?.provider === 'google' ? 'Google' : 'Microsoft'}
              </Box>
            </Box>
          </Box>
        </TabPanel>
      </Box>

      {/* グループ作成ダイアログ。中身は左メニューと共通の部品 */}
      <GroupFormDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        mode="create"
        name={newGroupName}
        onNameChange={setNewGroupName}
        description={newGroupDescription}
        onDescriptionChange={setNewGroupDescription}
        color={newGroupColor}
        onColorChange={setNewGroupColor}
        onSubmit={handleCreateGroup}
        submitting={loading}
      />

      {/* グループ編集ダイアログ。作成と同じ部品を使う */}
      <GroupFormDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        mode="edit"
        name={editGroupName}
        onNameChange={setEditGroupName}
        description={editGroupDescription}
        onDescriptionChange={setEditGroupDescription}
        color={editGroupColor}
        onColorChange={setEditGroupColor}
        onSubmit={handleUpdateGroup}
        submitting={loading}
      />

      {/* メンバー管理ダイアログ */}
      <AppDialog
        open={membersDialogOpen}
        onClose={() => setMembersDialogOpen(false)}
        icon={<Box component="img" src={IMG.settings.group} alt="" />}
        title="メンバー管理"
        subtitle={selectedGroup?.name}
        size="md"
      >
        {membersLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <List>
            {members.map((member) => (
              <ListItem key={member.id} divider>
                <ListItemAvatar>
                  <Avatar src={member.photoURL}>{member.displayName.charAt(0)}</Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={member.displayName}
                  secondary={
                    <Chip
                      label={member.role === 'owner' ? 'オーナー' : 'メンバー'}
                      size="small"
                      color={member.role === 'owner' ? 'primary' : 'default'}
                      variant="outlined"
                      sx={{ mt: 0.5 }}
                    />
                  }
                />
                {member.userId !== user?.uid && member.role !== 'owner' && (
                  <ListItemSecondaryAction>
                    <Tooltip title="オーナーに昇格">
                      <IconButton onClick={() => handlePromoteToOwner(member.id)} size="small">
                        <AdminIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="削除">
                      <IconButton
                        onClick={() => handleRemoveMember(member.id)}
                        size="small"
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </ListItemSecondaryAction>
                )}
              </ListItem>
            ))}
          </List>
        )}
      </AppDialog>

      {/* 招待リンクダイアログ */}
      <AppDialog
        open={inviteDialogOpen}
        onClose={() => setInviteDialogOpen(false)}
        icon={<Box component="img" src={IMG.workspace.invite} alt="" />}
        title="招待リンクを作る"
        subtitle="7日間有効なリンクを作って送ります"
        size="md"
        actions={
          <>
            {inviteLinks.length > 0 && (
              <Button onClick={handleCopyAllInviteLinks} sx={{ textTransform: 'none' }}>
                すべてコピー
              </Button>
            )}
            <Button
              variant="contained"
              onClick={handleCreateInvites}
              disabled={inviteLoading}
              sx={{ textTransform: 'none', fontWeight: 800, borderRadius: '10px' }}
            >
              {inviteLoading ? '作成中...' : 'リンクを作る'}
            </Button>
          </>
        }
      >
        {inviteLoading ? (
          <Box
            sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, gap: 2 }}
          >
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">
              {inviteCount}件のリンクを生成中...
            </Typography>
          </Box>
        ) : !inviteGenerated ? (
          <>
            <Alert severity="info" sx={{ mb: 3 }}>
              招待リンクは各1回のみ使用可能で、7日間有効です。招待したい人数分のリンクを生成してください。
            </Alert>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography>招待人数:</Typography>
              <TextField
                type="number"
                value={inviteCount}
                onChange={(e) =>
                  setInviteCount(Math.max(1, Math.min(20, parseInt(e.target.value, 10) || 1)))
                }
                slotProps={{
                  input: {
                    inputProps: { min: 1, max: 20 },
                  },
                }}
                size="small"
                sx={{ width: 80 }}
              />
              <Typography color="text.secondary">人</Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              ※ 最大20件まで一度に生成できます
            </Typography>
          </>
        ) : (
          <>
            <Alert severity="success" sx={{ mb: 2 }}>
              {inviteLinks.length}件の招待リンクを生成しました（7日間有効）
            </Alert>
            <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
              {inviteLinks.map((link, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 1,
                    p: 1,
                    bgcolor: 'rgba(255,255,255,0.05)',
                    borderRadius: 1,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      flex: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      fontFamily: 'monospace',
                      fontSize: '0.75rem',
                    }}
                  >
                    {link}
                  </Typography>
                  <Tooltip title="コピー">
                    <IconButton size="small" onClick={() => handleCopyInviteLink(link)}>
                      <CopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              ))}
            </Box>
          </>
        )}
      </AppDialog>

      {/* グループ削除確認ダイアログ */}
      <AppDialog
        open={deleteGroupDialogOpen}
        onClose={() => setDeleteGroupDialogOpen(false)}
        icon={<DeleteIcon />}
        title="グループを削除"
        subtitle="元に戻せません"
        size="sm"
        danger
        actions={
          <>
            <Button onClick={() => setDeleteGroupDialogOpen(false)} sx={{ textTransform: 'none' }}>
              キャンセル
            </Button>
            <Button
              onClick={handleDeleteGroup}
              variant="contained"
              disabled={deletingGroup}
              sx={{
                textTransform: 'none',
                fontWeight: 800,
                borderRadius: '10px',
                bgcolor: '#d4433d',
              }}
            >
              {deletingGroup ? '削除中...' : '削除する'}
            </Button>
          </>
        }
      >
        <Alert severity="warning" sx={{ mb: 2 }}>
          この操作は取り消せません
        </Alert>
        <Typography variant="body1" sx={{ mb: 2 }}>
          「<strong>{groupToDelete?.name}</strong>」を削除しますか？
        </Typography>
        <Typography variant="body2" color="text.secondary">
          このグループに関連するすべての案件とメンバーシップも削除されます。
          グループメンバー全員がこのグループとその関連データにアクセスできなくなります。
        </Typography>
      </AppDialog>

      {/* スナックバー */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Settings;
